import {
  FormatDistanceToNowOptions,
  FormatDistanceToken,
  FormatDurationOptions,
  addDays,
  format as dateFnsFormat,
  differenceInCalendarDays,
  formatDistance,
  formatDistanceToNow,
  formatDuration,
  intervalToDuration,
  isBefore,
  isSameDay,
  isValid,
  parse,
  parseISO,
  startOfWeek,
  subYears,
} from "date-fns";

import { i18n, languageLookup } from "./i18n";
import { DEFAULT_ORG_TIMEZONE } from "./timezones";

const getLocale = (language: string) => {
  return (
    languageLookup.get(language)?.locale || languageLookup.get("en")!.locale
  );
};

// Dates at or before this are considered invalid (e.g. new Date(null) = epoch)
const MIN_VALID_DATE = new Date("1971-01-01T00:00:00.000Z");

let orgTimezone: string | null = null;

export const setOrgTimezone = (timezone?: string | null) => {
  orgTimezone = timezone || null;
};

const getBrowserTimezone = (): string | null => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    return null;
  }
};

export const getTimezone = (): string =>
  getBrowserTimezone() || orgTimezone || DEFAULT_ORG_TIMEZONE;

const toZonedDate = (date: Date, timeZone: string): Date => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value || "0";

  return new Date(
    parseInt(get("year"), 10),
    parseInt(get("month"), 10) - 1,
    parseInt(get("day"), 10),
    parseInt(get("hour"), 10),
    parseInt(get("minute"), 10),
    parseInt(get("second"), 10),
  );
};

export const parseDate = (
  value: string | Date | number | null | undefined,
  dateFormat?: string,
): Date | null => {
  if (value === null || value === undefined) return null;
  let date: Date | null = null;
  if (value instanceof Date) {
    date = value;
  } else if (typeof value === "string") {
    if (dateFormat) {
      date = parse(value, dateFormat, new Date());
    } else {
      date = parseISO(value);
    }
  } else if (typeof value === "number") {
    date = new Date(value);
  }

  if (!date || !isValid(date) || date <= MIN_VALID_DATE) return null;
  return date;
};

export const formatDate = (
  value: string | Date | number | null | undefined,
  dateFormat: string = "dd/MM/yyyy HH:mm",
): string => {
  const date = parseDate(value);
  if (!date) return "";

  const browserTimezone = getBrowserTimezone();
  const displayDate =
    browserTimezone ? date : (
      toZonedDate(date, orgTimezone || DEFAULT_ORG_TIMEZONE)
    );

  return dateFnsFormat(displayDate, dateFormat, {
    locale: getLocale(i18n.language),
  });
};

export const formatDateRelative = (
  value: string | Date | number | null | undefined,
  options?: FormatDistanceToNowOptions,
): string => {
  const date = parseDate(value);
  if (!date) return "";

  return formatDistanceToNow(date, {
    ...options,
    locale: getLocale(i18n.language),
  });
};

export const formatDateAt = (
  value: string | Date | number | null | undefined,
): string => {
  const date = parseDate(value);
  if (!date) return "";

  const now = new Date();
  const time = formatDate(date, "HH:mm");
  const daysAgo = differenceInCalendarDays(now, date);

  if (daysAgo === 0) {
    return `${i18n.t("datetime.today")} ${i18n.t("datetime.at")} ${time}`;
  }
  if (daysAgo === 1) {
    return `${i18n.t("datetime.yesterday")} ${i18n.t("datetime.at")} ${time}`;
  }
  if (isBefore(date, subYears(now, 1))) {
    return `${formatDate(date, "EEEE, d MMMM yyyy")} ${i18n.t("datetime.at")} ${time}`;
  }
  return `${formatDate(date, "EEEE, d MMMM")} ${i18n.t("datetime.at")} ${time}`;
};

export const formatDateDistance = (
  dateLeft: string | Date | number | null | undefined,
  dateRight: string | Date | number | null | undefined,
  options?: { addSuffix?: boolean; includeSeconds?: boolean },
): string => {
  const left = parseDate(dateLeft);
  const right = parseDate(dateRight);
  if (!left || !right) return "";
  return formatDistance(left, right, {
    ...options,
    locale: getLocale(i18n.language),
  });
};

export const datePlusDays = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

export const dateStart = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

export const dateEnd = (date: Date): Date => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

export const formatSecondsToDuration = (
  seconds: number,
  options?: Omit<FormatDurationOptions, "format"> & {
    format?: {
      years?: string;
      months?: string;
      weeks?: string;
      days?: string;
      hours?: string;
      minutes?: string;
      seconds?: string;
    };
  },
): string => {
  const { format, ...restOptions } = options || {};
  if (format) {
    const { years, months, weeks, days, hours, minutes, seconds } = format;

    const formatDistanceLocale: Partial<Record<FormatDistanceToken, string>> = {
      xYears: years && `{{count}}${years}`,
      xMonths: months && `{{count}}${months}`,
      xWeeks: weeks && `{{count}}${weeks}`,
      xDays: days && `{{count}}${days}`,
      xHours: hours && `{{count}}${hours}`,
      xMinutes: minutes && `{{count}}${minutes}`,
      xSeconds: seconds && `{{count}}${seconds}`,
    };
    const shortEnLocale = {
      formatDistance: (token: FormatDistanceToken, count: number) => {
        let val = (count || "00").toString();
        if (val.length < 2 && restOptions.zero) {
          val = "0" + val;
        }
        return formatDistanceLocale[token]?.replace("{{count}}", val) || "";
      },
    };
    restOptions.locale = shortEnLocale;
  } else {
    restOptions.locale = getLocale(i18n.language);
  }

  return formatDuration(
    intervalToDuration({ start: 0, end: seconds * 1000 }),
    restOptions,
  );
};

export const getDayOfWeekAsDate = (dayOfWeek: number): Date => {
  const startOfWeekDay = startOfWeek(new Date());
  const daysToAdd = [0, 1, 2, 3, 4, 5, 6].indexOf(dayOfWeek);
  let currentDate = addDays(startOfWeekDay, daysToAdd);

  // Ensure we're not returning a past date
  if (isSameDay(currentDate, startOfWeekDay)) {
    currentDate = addDays(currentDate, 7);
  }

  return currentDate;
};
